import { DateTime } from 'luxon'
import { useRef } from 'react'
import { useReselector, useTimeoutRef } from '@/utils/sharedHooks'
import {
  selectCatchUpBookkeepingStatusForCurrentUser,
  selectIsCatchUpBookkeepingComplete,
} from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.selector'
import {
  fetchUserOnboardingStepsIfNeeded,
  updateUserOnboardingStep,
} from './onboarding.actions'
import {
  GettingStartedOnboardingStep,
  OnboardingStepId,
  OnboardingStepPhase,
} from './onboarding.reducer'
import {
  getAllOnboardingSteps,
  getUserOnboardingStepByOnboardingStepId,
  getUserOnboardingSteps,
  selectUserShouldSeeOnboardingDashboard,
} from './onboarding.selectors'
import { useAppDispatch } from '@/utils/typeHelpers'
import { selectCubkDisplayPhase } from '@/features/user/dashboard/onboarding/catch-up-bookkeeping/cubk.selectors'
import { selectCubkProgressData } from '@/features/user/dashboard/onboarding/catch-up-bookkeeping/lib/cubkProgress.selectors'
import { FETCH_CUBK_PROGRESS_KEY } from '@/features/user/dashboard/onboarding/catch-up-bookkeeping/lib/cubkProgress.slice'
import { CubkCardState } from '@/features/user/dashboard/onboarding/catch-up-bookkeeping/lib/cubkProgress.types'
import { getFetchError } from '@/reducers/fetch'

const REFETCH_ONBOARDING_STEPS_TIMEOUT = 2000

export const useCompleteOnboardingStepIfNeeded = (
  stepIdentifier: OnboardingStepId
) => {
  const dispatch = useAppDispatch()
  const userOnboardingStep = useReselector(
    getUserOnboardingStepByOnboardingStepId,
    stepIdentifier
  )
  const fetchUserOnboardingStepsTimeout = useTimeoutRef()
  /**
   *  Marks the onboarding step as complete if it hasn't been already and conditionally re-fetches all onboarding steps
   *  We conditionally re-fetch all onboarding steps because the user may have completed a step that unlocks other steps
   * @param shouldRefetchAllOnboardingSteps - If true, fetch all onboarding steps after marking the step as complete
   */
  const markComplete = async ({
    shouldRefetchAllOnboardingSteps,
  }: {
    shouldRefetchAllOnboardingSteps?: boolean
  } = {}) => {
    if (!userOnboardingStep) {
      return
    }

    if (!userOnboardingStep.completedAt) {
      await updateUserOnboardingStep({
        id: userOnboardingStep.id,
        updatableFields: { completedAt: DateTime.now().toISO() },
      })(dispatch)
      if (shouldRefetchAllOnboardingSteps) {
        fetchUserOnboardingStepsTimeout.current = setTimeout(() => {
          // Use timeout because we want to give the backend time to create or update
          // any dependent steps before fetching
          dispatch(
            fetchUserOnboardingStepsIfNeeded({
              expanded: true,
              alwaysFetch: true,
            })
          )
        }, REFETCH_ONBOARDING_STEPS_TIMEOUT)
      }
    }
  }
  return { markComplete }
}

/**
 * Wraps selectUserShouldSeeOnboardingDashboard with extra rules:
 *  - Excludes JOIN_GROUP_ONBOARDING_SESSION from the completion gate.
 *  - Keeps the dashboard visible for one session after CUBK completes so the
 *    user sees the "Books complete" card (persisted via `celebrationSeenAt`
 *    on the user's `CatchUpBookkeepingStatus`, so it shows once per user
 *    across browsers).
 */
export const useSelectUserShouldSeeOnboardingDashboard = (): boolean => {
  const baseResult = useReselector(selectUserShouldSeeOnboardingDashboard)
  const userOnboardingSteps = useReselector(getUserOnboardingSteps)
  const allOnboardingSteps = useReselector(getAllOnboardingSteps)
  const catchupBKIsComplete = useReselector(selectIsCatchUpBookkeepingComplete)
  const status = useReselector(selectCatchUpBookkeepingStatusForCurrentUser)
  // Capture once status is defined. A previous useState + useEffect approach
  // produced a one-render `null` gap where the hook returned false before the
  // post-commit setState ran, which caused `OnboardingDashboard`'s navigate
  // effect to bounce the user back to `/dashboard`, unmount the component,
  // reset state, and loop. Computing synchronously during render eliminates
  // the gap. The `=== null` guard makes the assignment idempotent.
  const seenInPriorSessionRef = useRef<boolean | null>(null)
  if (
    seenInPriorSessionRef.current === null &&
    status !== undefined &&
    status !== null
  ) {
    seenInPriorSessionRef.current =
      status.celebrationSeenAt !== null &&
      status.celebrationSeenAt !== undefined
  }
  const seenInPriorSession = seenInPriorSessionRef.current

  const cubkDisplayPhase = useReselector(selectCubkDisplayPhase)
  const cubkProgressData = useReselector(selectCubkProgressData)
  const cubkProgressFetchFailed = useReselector(
    getFetchError,
    FETCH_CUBK_PROGRESS_KEY
  )
  if (!catchupBKIsComplete) {
    return baseResult
  }

  if (!baseResult) {
    if (
      cubkProgressData === null &&
      !cubkProgressFetchFailed &&
      seenInPriorSession === false
    ) {
      return true
    }
    if (
      cubkDisplayPhase === CubkCardState.COMPLETE &&
      seenInPriorSession === false
    ) {
      return true
    }
    return false
  }

  if (
    cubkProgressData === null &&
    !cubkProgressFetchFailed &&
    seenInPriorSession === false
  ) {
    return true
  }

  if (
    cubkDisplayPhase === CubkCardState.COMPLETE &&
    seenInPriorSession === false
  ) {
    return true
  }

  const gettingStartedStepIds = Object.values(allOnboardingSteps)
    .filter((step) => step.phase === OnboardingStepPhase.gettingStarted)
    .map((step) => step.identifier)

  const userGettingStartedSteps = Object.values(userOnboardingSteps).filter(
    (userStep) => gettingStartedStepIds.includes(userStep.onboardingStepId)
  )

  const stepsExcludingJoinGroup = userGettingStartedSteps.filter(
    (step) =>
      step.onboardingStepId !==
      GettingStartedOnboardingStep.joinGroupOnboardingSession
  )

  if (stepsExcludingJoinGroup.length === 0) {
    return false
  }

  const allOtherStepsComplete = stepsExcludingJoinGroup.every(
    (step) => step.completedAt || step.skippedAt
  )

  return !allOtherStepsComplete
}
